import type { MobilePreset } from '@/lib/api/controlPlaneClient'

export type NavStyle = 'bottom-tab-bar' | 'hamburger' | 'floating-icons'
export type SearchPlacement = 'sticky-top' | 'in-menu' | 'floating'
export type CategoryLayout = 'icon-grid' | 'sidebar-filter' | 'horizontal-chips'
export type ProductListLayout = 'grid-2col' | 'grid-3col'
export type TableRowDensity = 'compact' | 'normal' | 'relaxed'
export type TableImageSize = 'hidden' | 'small' | 'medium' | 'large'
export type DefaultView = 'grid' | 'compact' | 'table'

export interface PresetDefaults {
  nav_style: NavStyle
  search_placement: SearchPlacement
  category_layout: CategoryLayout
  product_list_layout: ProductListLayout
  table_row_density: TableRowDensity
  table_image_size: TableImageSize
  default_view: DefaultView
}

export const PRESET_DEFAULTS: Record<MobilePreset, PresetDefaults> = {
  'app-style': {
    nav_style: 'bottom-tab-bar',
    search_placement: 'sticky-top',
    category_layout: 'icon-grid',
    product_list_layout: 'grid-2col',
    table_row_density: 'normal',
    table_image_size: 'medium',
    default_view: 'grid',
  },
  'web-style': {
    nav_style: 'hamburger',
    search_placement: 'in-menu',
    category_layout: 'sidebar-filter',
    product_list_layout: 'grid-2col',
    table_row_density: 'normal',
    table_image_size: 'medium',
    default_view: 'grid',
  },
  minimal: {
    nav_style: 'floating-icons',
    search_placement: 'floating',
    category_layout: 'horizontal-chips',
    product_list_layout: 'grid-2col',
    table_row_density: 'compact',
    table_image_size: 'small',
    default_view: 'compact',
  },
}

export const OVERRIDE_FIELDS = [
  'nav_style',
  'search_placement',
  'category_layout',
  'product_list_layout',
  'table_row_density',
  'table_image_size',
  'default_view',
] as const

export type OverrideField = (typeof OVERRIDE_FIELDS)[number]

export const OVERRIDE_OPTIONS: Record<
  OverrideField,
  ReadonlyArray<{ value: string; label: string }>
> = {
  nav_style: [
    { value: 'bottom-tab-bar', label: 'แถบด้านล่าง (Bottom tabs)' },
    { value: 'hamburger', label: 'แฮมเบอร์เกอร์ (Hamburger)' },
    { value: 'floating-icons', label: 'ไอคอนลอย (Floating icons)' },
  ],
  search_placement: [
    { value: 'sticky-top', label: 'ติดด้านบน (Sticky top)' },
    { value: 'in-menu', label: 'ในเมนู (In menu)' },
    { value: 'floating', label: 'ไอคอนลอย (Floating icon)' },
  ],
  category_layout: [
    { value: 'icon-grid', label: 'ไอคอนกริด (Icon grid)' },
    { value: 'horizontal-chips', label: 'เลื่อนแนวนอน (Horizontal chips)' },
    { value: 'sidebar-filter', label: 'แถบด้านข้าง (Sidebar filter)' },
  ],
  product_list_layout: [
    { value: 'grid-2col', label: '2 คอลัมน์ (2-column)' },
    { value: 'grid-3col', label: '3 คอลัมน์ (3-column compact)' },
  ],
  table_row_density: [
    { value: 'compact', label: 'กระชับ (Compact)' },
    { value: 'normal', label: 'ปกติ (Normal)' },
    { value: 'relaxed', label: 'สบาย (Relaxed)' },
  ],
  table_image_size: [
    { value: 'hidden', label: 'ซ่อน (Hidden)' },
    { value: 'small', label: 'เล็ก (Small — 36px)' },
    { value: 'medium', label: 'กลาง (Medium — 48px)' },
    { value: 'large', label: 'ใหญ่ (Large — 64px)' },
  ],
  default_view: [
    { value: 'grid', label: 'กริด (Grid)' },
    { value: 'compact', label: 'กระชับ (Compact)' },
    { value: 'table', label: 'ตาราง (Table)' },
  ],
}

export const OVERRIDE_LABELS: Record<OverrideField, string> = {
  nav_style: 'รูปแบบเมนู (Navigation)',
  search_placement: 'ตำแหน่งค้นหา (Search)',
  category_layout: 'รูปแบบหมวดหมู่ (Categories)',
  product_list_layout: 'รูปแบบสินค้า (Product grid)',
  table_row_density: 'ความหนาแน่นของแถว (Row Density)',
  table_image_size: 'ขนาดรูปภาพ (Image Size)',
  default_view: 'มุมมองเริ่มต้น (Default View)',
}

// --- Column visibility (separate from OverrideSelect dropdowns) ---

export interface ColumnVisibility {
  table_show_sku: boolean
  table_show_collections: boolean
  table_show_price: boolean
  table_show_add_to_cart: boolean
}

export const COLUMN_FIELDS = [
  'table_show_sku',
  'table_show_collections',
  'table_show_price',
  'table_show_add_to_cart',
] as const

export type ColumnField = (typeof COLUMN_FIELDS)[number]

export const COLUMN_LABELS: Record<ColumnField, string> = {
  table_show_sku: 'SKU',
  table_show_collections: 'Collections',
  table_show_price: 'ราคา (Price)',
  table_show_add_to_cart: 'เพิ่มลงตะกร้า (Add to Cart)',
}

export const COLUMN_DEFAULTS: Record<MobilePreset, ColumnVisibility> = {
  'app-style': {
    table_show_sku: true,
    table_show_collections: true,
    table_show_price: true,
    table_show_add_to_cart: true,
  },
  'web-style': {
    table_show_sku: false,
    table_show_collections: true,
    table_show_price: true,
    table_show_add_to_cart: true,
  },
  minimal: {
    table_show_sku: false,
    table_show_collections: false,
    table_show_price: true,
    table_show_add_to_cart: true,
  },
}
